// ─────────────────────────────────────────────────────────────────────────────
// Kitchen chimes, synthesised rather than shipped.
//
// Two reasons not to bundle audio files: a kitchen tablet on a congested
// in-store wifi should not be fetching assets to make a noise, and an offline
// or cache-cold screen must still be audible — the chime is the only thing that
// tells a cook facing the grill that an order landed.
//
// A short WebAudio envelope costs nothing, has no network dependency and cannot
// be broken by a stale cache. It is also easy to keep DISTINCT: a new order and
// a ticket going critical must not sound alike, or the second one stops
// meaning anything.
//
// Autoplay policy: browsers refuse to start an AudioContext until the user has
// interacted with the page. On a wall-mounted screen the first interaction is
// picking a station, which is enough. Until then `resume()` rejects silently
// and we simply make no sound — never an error, never a blocked render.
// ─────────────────────────────────────────────────────────────────────────────

type Chime = 'new' | 'late';

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Two-note rising figure for a new ticket, a lower insistent double for a late
 * one. Deliberately short (under 300ms) — a kitchen is loud and a long chime
 * just overlaps the next one.
 */
const VOICES: Record<Chime, Array<{ freq: number; at: number; dur: number }>> = {
  new: [
    { freq: 880, at: 0, dur: 0.12 },
    { freq: 1320, at: 0.11, dur: 0.16 },
  ],
  late: [
    { freq: 440, at: 0, dur: 0.14 },
    { freq: 440, at: 0.2, dur: 0.14 },
  ],
};

export function chime(kind: Chime): void {
  const audio = audioContext();
  if (!audio) return;

  // Suspended until the first user gesture. resume() is a promise that can
  // reject; swallow it, because a failed chime must never surface as an error.
  if (audio.state === 'suspended') void audio.resume().catch(() => undefined);

  try {
    const now = audio.currentTime;
    for (const note of VOICES[kind]) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = note.freq;

      // A hard start/stop on a sine wave clicks. Ramping the gain in and out
      // over a few milliseconds is the difference between a chime and a pop.
      const start = now + note.at;
      const end = start + note.dur;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);

      osc.connect(gain).connect(audio.destination);
      osc.start(start);
      osc.stop(end + 0.02);
    }
  } catch {
    // Any WebAudio failure is silent by design.
  }
}

/**
 * Nudge the audio context alive from a real user gesture.
 *
 * Called from the first tap anywhere on the board. Without it, a screen that is
 * never touched (a wall panel) stays muted — which is the correct browser
 * behaviour and, awkwardly, exactly the screen that most needs the sound. One
 * tap at the start of service fixes it for the whole shift.
 */
export function unlockAudio(): void {
  const audio = audioContext();
  if (audio && audio.state === 'suspended') void audio.resume().catch(() => undefined);
}
